import React from 'react';
import { ChefHat } from 'lucide-react';

const AboutSection = () => {
  return (
    <section id="about" className="py-32 bg-black/80 relative">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-24 animate-fade-in-up">
          <div className="mb-8">
            <ChefHat className="mx-auto text-red-600 opacity-80" size={48} />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-thin tracking-[0.2em] md:tracking-[0.3em] text-white mb-8 leading-tight">
            NUESTRA HISTORIA
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-red-600 to-transparent mx-auto mb-12"></div>
          <p className="text-lg text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
            Una tradición culinaria de cocina tradicional francesa y belga con elaboración casera
          </p>
        </div>

        {/* Restaurant Story */}
        <div className="grid lg:grid-cols-2 gap-20 items-center mb-32">
          {/* Text Content */}
          <div className="space-y-8 animate-slide-in-left">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-thin tracking-[0.15em] md:tracking-[0.2em] text-white mb-8 leading-tight">
              ELEGANCIA Y TRADICIÓN EN ALTEA LA VELLA
            </h3>
            
            <div className="space-y-6 text-gray-300 font-light leading-relaxed">
              <p className="text-lg">
                Desde su apertura, <span className="text-white font-medium">Nou Valentino</span> se ha establecido como un referente de la alta cocina 
                en la Costa Blanca. Ubicado en el corazón histórico de Altea la Vella, nuestro restaurante se especializa 
                en la sofisticación culinaria francesa y belga con elaboración completamente casera.
              </p>
              
              <p className="text-lg">
                Nuestros comensales destacan constantemente la <em className="text-red-400">excepcional calidad</em> de nuestros platos, 
                la <em className="text-red-400">elegancia del ambiente</em> y el <em className="text-red-400">servicio impecable</em>. Cada experiencia gastronómica 
                es cuidadosamente orquestada para crear momentos memorables que reflejan nuestra pasión 
                por la excelencia culinaria.
              </p>
              
              <p className="text-lg">
                Nuestra especialización en <span className="text-white font-medium">cocina tradicional francesa y belga</span> con ingredientes frescos locales 
                ha creado una propuesta gastronómica única que atrae a comensales exigentes de toda Europa. 
                Nuestro compromiso con la elaboración casera y la calidad nos ha convertido en un destino culinario 
                de referencia en Altea la Vella.
              </p>
            </div>

            <div className="bg-gradient-to-r from-red-900/30 to-red-800/30 border border-red-800/50 p-8 backdrop-blur-sm">
              <h4 className="text-xl font-light tracking-wider text-white mb-6">LO QUE NOS DISTINGUE</h4>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-center text-sm tracking-wide">
                  <span className="w-2 h-px bg-red-600 mr-4"></span>
                  Cocina francesa de alta gama
                </li>
                <li className="flex items-center text-sm tracking-wide">
                  <span className="w-2 h-px bg-red-600 mr-4"></span>
                  Ambiente elegante y sofisticado
                </li>
                <li className="flex items-center text-sm tracking-wide">
                  <span className="w-2 h-px bg-red-600 mr-4"></span>
                  Ingredientes frescos y selectos
                </li>
                <li className="flex items-center text-sm tracking-wide">
                  <span className="w-2 h-px bg-red-600 mr-4"></span>
                  Servicio personalizado y atento
                </li>
                <li className="flex items-center text-sm tracking-wide">
                  <span className="w-2 h-px bg-red-600 mr-4"></span>
                  Experiencia gastronómica inolvidable
                </li>
              </ul>
            </div>
          </div>

          {/* Restaurant Images */}
          <div className="grid grid-cols-2 gap-6 animate-slide-in-right">
            <div className="space-y-6">
              <div className="group aspect-[4/3] overflow-hidden luxury-shadow relative">
                <img 
                  src="/images/restaurant_terrace_evening.jpg" 
                  alt="Terraza nocturna de Nou Valentino" 
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h4 className="text-sm font-light tracking-wider">TERRAZA NOCTURNA</h4>
                </div>
              </div>
              
              <div className="group aspect-[4/3] overflow-hidden luxury-shadow relative">
                <img 
                  src="/images/restaurant_interior_elegant.jpg" 
                  alt="Interior elegante del restaurante" 
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h4 className="text-sm font-light tracking-wider">INTERIOR ELEGANTE</h4>
                </div>
              </div>
            </div>
            
            <div className="space-y-6 mt-12">
              <div className="group aspect-[4/3] overflow-hidden luxury-shadow relative">
                <img 
                  src="/images/restaurant_dining_room.jpg" 
                  alt="Comedor principal" 
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h4 className="text-sm font-light tracking-wider">COMEDOR PRINCIPAL</h4>
                </div>
              </div>
              
              <div className="group aspect-[4/3] overflow-hidden luxury-shadow relative">
                <img 
                  src="/images/restaurant_atmosphere.jpg" 
                  alt="Ambiente acogedor" 
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h4 className="text-sm font-light tracking-wider">AMBIENTE ACOGEDOR</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chef Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/50 to-red-900/20"></div>
          <div className="relative backdrop-blur-sm border border-red-900/30 p-12 md:p-16">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              {/* Chef Photo */}
              <div className="order-2 lg:order-1 animate-scale-in">
                <div className="relative group">
                  <div className="aspect-[4/5] overflow-hidden luxury-shadow">
                    <img 
                      src="/images/chef_luc_carlier.jpg" 
                      alt="Chef Luc Carlier de Nou Valentino" 
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 group-hover:brightness-110"
                    />
                  </div>
                  <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-red-600 to-red-800 text-white p-6 backdrop-blur-sm border border-red-500/50">
                    <ChefHat size={40} />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>

              {/* Chef Content */}
              <div className="order-1 lg:order-2 space-y-8 animate-fade-in-up">
                <div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-thin tracking-[0.15em] md:tracking-[0.2em] text-white mb-4 leading-tight">
                    CHEF LUC CARLIER
                  </h3>
                  <div className="w-16 h-px bg-red-600 mb-6"></div>
                  <p className="text-lg text-red-400 font-light tracking-wide">Maestro de la Haute Cuisine</p>
                </div>

                <div className="space-y-6 text-gray-300 font-light leading-relaxed">
                  <p className="text-lg">
                    Con más de dos décadas de experiencia en la alta cocina europea, el <span className="text-white font-medium">Chef Luc Carlier</span>  es el
                    alma creativa detrás de cada plato que sale de nuestra cocina. Su formación en prestigiosos 
                    restaurantes franceses y belgas y su pasión por los ingredientes frescos se reflejan en cada creación casera.
                  </p>

                  <p className="text-lg">
                    Su filosofía culinaria se basa en el respeto por los productos de temporada, la precisión técnica 
                    y la presentación artística. Cada plato es una obra de arte que combina sabores tradicionales 
                    con platos sofisticados de elaboración casera, creando experiencias gastronómicas que despiertan todos los sentidos.
                  </p>
                </div>

                <div className="bg-black/30 border border-red-900/30 p-8 backdrop-blur-sm">
                  <h4 className="text-lg font-light tracking-wider text-white mb-6">ESPECIALIDADES DEL CHEF</h4>
                  <ul className="space-y-4 text-gray-300">
                    <li className="flex items-center text-sm tracking-wide">
                      <span className="w-2 h-px bg-red-600 mr-4"></span>
                      Cocina francesa clásica y moderna
                    </li>
                    <li className="flex items-center text-sm tracking-wide">
                      <span className="w-2 h-px bg-red-600 mr-4"></span>
                      Especialidades francesas y belgas caseras
                    </li>
                    <li className="flex items-center text-sm tracking-wide">
                      <span className="w-2 h-px bg-red-600 mr-4"></span>
                      Mariscos y pescados frescos
                    </li>
                    <li className="flex items-center text-sm tracking-wide">
                      <span className="w-2 h-px bg-red-600 mr-4"></span>
                      Postres artesanales únicos
                    </li>
                  </ul>
                </div>

                <blockquote className="border-l-2 border-red-600 pl-8 italic text-lg text-gray-300 font-light">
                  "La cocina es poesía en movimiento. Cada ingrediente cuenta una historia, 
                  y nosotros somos los narradores de esas historias en el plato."
                  <footer className="text-red-400 font-medium mt-4 text-base not-italic tracking-wider">
                    — CHEF LUC CARLIER
                  </footer>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;